import { MenuFactory } from 'sharebase/factories/menuFactory';
import {
	Menu,
	IMenuItem
} from 'sharebase/domain';

export const gridTemplate = require('./grid.template');
require('./grid.styles');

export interface GridInitSort {
	query:GridQuery;
	filterDirection:boolean;
}

export interface GridConfig {
	actionLabel:boolean;
	hasAvatar:boolean;
	maxHeight:number;
	virtualize:boolean;
	initSort:GridInitSort;
}

export enum ColumnType {
	Text = 'Text',
	Date = 'Date'
}

interface GridRow<T> {
	type:string;
	icon:string;
	data:RowData;
	source:T;
	isDisabled:boolean;
	trigger:() => void;
}

interface GridColumn<T> { 
	label:string;
	width?:string;
	sortColumn:string;
	type:ColumnType;
	sortable:boolean;
	validator?:(source:T[]) => boolean;
}

interface GridQuery {
	order:string;
	filter:string;
}

interface RowData {
	[sortColumn:string]:CellContent
}

interface CellContent {
	text:string|Date;
	subtext?:string;
}

export interface GridEmptyState {
	icon:string;
	text:string;
	subtext?:string;
}

interface ShareBaseGridScope<T> extends ng.IScope{
	vm:Grid<T>;
}

export abstract class Grid<T> implements ng.IOnInit{
	constructor(
		private $scope:ng.IScope,
		private sbMenuFactory:MenuFactory
	) {
		this.actions.forEach(action => {
			this.hoverMenu.addItem(action);
		});
	}
	
	public $onInit() {
		this.$scope.$on('$mdMenuOpen', () => this.menuOpened = true);
		this.$scope.$on('$mdMenuClose', () => this.menuOpened = false);
		this.$scope.$watch('vm.source', (newVal:T[], oldVal:T[], scope:ShareBaseGridScope<T>) => {
			if (newVal) {
				scope.vm.rows = newVal.map(i => scope.vm.transform(i));
			}
		}, true);
	}

	public source:T[];
	public config:GridConfig;
	public actions:IMenuItem[];
	public emptyState:GridEmptyState;

	public rows:GridRow<T>[];
	public columns:GridColumn<T>[];
	
	public rowMaxShow:number = 15;
	public hoverMenu:Menu = this.sbMenuFactory.create();
	public currentRow:GridRow<T>;
	private menuOpen:boolean;
	public get menuOpened() {
		return this.menuOpen;
	}
	public set menuOpened(open:boolean) {
		this.menuOpen = open;

		if(!open){
			this.currentRow = null;
		}
	}
	
	public query:GridQuery = this.config.initSort.query;
	private _filterDirection:boolean = this.config.initSort.filterDirection;
	public filter:string;
	public filterColumns = [];
	
	public abstract transform:(source:T) => GridRow<T>;
	public leaveRow() {
		if (this.menuOpen) {
			return;
		}
		this.currentRow = null;
	}

	public resolveActions(row:GridRow<T>) {
		return this.hoverMenu.getActiveItems(row.source);
	}

	public hasActions() {
		return Object.keys(this.hoverMenu.items).some(item => this.hoverMenu.items[item].visible);
	}

	public canShow(column:GridColumn<T>) {
		return column.validator ? column.validator(this.source) : true;
	}

	public setFilter(item) {
		if(item.sortColumn === this.query.order){
			//flip sorting
			this._filterDirection = !this._filterDirection;
			return;
		}

		//new column sort
		this._filterDirection = false;
		this.query.order = item.sortColumn;
	}
	public get filterDirection() {
		return this._filterDirection;
	};
	public get filterProp() {
		return this.query.order === 'type' ? 'type' : `data.${this.query.order}.text`;
	}
	public excludeInFilter(column:GridColumn<T>){
		if(this.filterColumns.length === 0 || !this.filterColumns[0]){
			return false;
		}
		return !this.filterColumns.some( f => f.sortColumn === column.sortColumn); 
	}
	public filterRows(row){
		if(!this.filter || this.filter === ''){
			return row;
		}
		
		if(this.filterColumns.length>0 && this.filterColumns[0]){
			let include = this.filterColumns.filter(f=>{
				let parts = [];
				if(row.data[f.sortColumn].text){parts.push(row.data[f.sortColumn].text)};
				if(row.data[f.sortColumn].subtext){parts.push(row.data[f.sortColumn].subtext)};
				let text = parts.join(' ');
				return text.toLowerCase().indexOf(this.filter.toLowerCase()) > -1;
			});

			return include.length>0? row: null;
		}
		else{
			let text;
			Object.keys(row.data).forEach(d => {
				let col = this.columns.filter(c => c.sortColumn === d);
				if(col.length === 1){
					if(this.canShow(col[0])){
						text = text + ' ^ ' + [row.data[d].text, row.data[d].subtext].join(' '); 
					}	
				}
			});
			return text ? text.toLowerCase().indexOf(this.filter.toLowerCase()) > -1 : true;
		}
	}
}

export class ShareBaseGrid<T> extends Grid<T> implements ng.IComponentController {
	public static $inject = [
		'$scope',
		'sbMenuFactory',
		'gettextCatalog'
	];

	public transformSource:(source:T) => GridRow<T>;

	constructor(
		$scope:ng.IScope,
		sbMenuFactory:MenuFactory,
		private gettextCatalog:ng.gettext.gettextCatalog
	) {
		super($scope,sbMenuFactory);
	}

	private placeholderText = {
		filterString: this.gettextCatalog.getString('Filter text'),
		columnSelect: this.gettextCatalog.getString('All Columns')
	};

	public transform = (source:T) => {
		return this.transformSource(source);
	};
}

export const ShareBaseGridComponent:ng.IComponentOptions = {
	template: gridTemplate,
	controller: ShareBaseGrid,
	controllerAs: 'vm',
	bindings: {
		source: '<',
		config: '<',
		transformSource: '<transform',
		actions: '<',
		emptyState: '<',
		columns: '<'
	}
}


// WEBPACK FOOTER //
// ./src/sharebase/components/grid/grid.ts